import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import BASEURL from '../API/Config';

const PurchaseList = () => {
  const [purchases, setPurchases] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editPurchase, setEditPurchase] = useState(null);
  const token = localStorage.getItem('token');

  const fetchPurchases = async () => {
    try {
      const response = await fetch(`${BASEURL}/master/getpurchase`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch purchases');

      let purchasesData = await response.json();

      // Fetch vendor details
      const vendorResponse = await fetch(`${BASEURL}/master/getvendor1`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!vendorResponse.ok) throw new Error('Failed to fetch vendors');

      const vendorsData = await vendorResponse.json();

      // Convert vendors array into a lookup object { vendorId: "Full Name" }
      const vendorMap = vendorsData.reduce((acc, vendor) => {
        acc[vendor._id] = `${vendor.firstname} ${vendor.lastname}`;
        return acc;
      }, {});

      // Replace vendor ID with vendor name
      purchasesData = purchasesData.map((purchase) => ({
        ...purchase,
        vendorName: vendorMap[purchase.vendor] || 'Unknown Vendor',
      }));

      setPurchases(purchasesData);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load purchases.',
      });
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const handleEdit = (purchase) => {
    setIsEditing(true);
    setEditPurchase(purchase);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const { purchaseNo, vendorName, totalAmount, items } = editPurchase;

    if (!purchaseNo || !vendorName || !totalAmount || !items?.length) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'All fields are required.',
      });
      return;
    }

    try {
      const response = await fetch(`${BASEURL}/master/update/${editPurchase._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editPurchase),
      });

      if (!response.ok) throw new Error('Failed to update purchase');

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Purchase updated successfully!',
      });

      fetchPurchases();
      setIsEditing(false);
      setEditPurchase(null);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update purchase.',
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${BASEURL}/master/delete/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Purchase deleted successfully!',
      });

      fetchPurchases();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete purchase.',
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditPurchase((prevPurchase) => ({
      ...prevPurchase,
      [name]: value,
    }));
  };

  return (
    <div className="container mt-5">
      <h4 className="mb-4 text-center">Purchase List</h4>
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>S.No</th>
              <th>Order Number</th>
              <th>Vendor Name</th>
              <th>Total Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchases.length > 0 ? (
              purchases.map((purchase, index) => (
                <tr key={purchase._id}>
                  <td>{index + 1}</td>
                  <td>{purchase.purchaseNo}</td>
                  <td>{purchase.vendorName}</td>
                  <td>{purchase.totalAmount}</td>
                  <td>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleEdit(purchase)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm ms-2"
                      onClick={() => handleDelete(purchase._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  No purchases found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isEditing && editPurchase && (
        <div className="mt-4 p-4 shadow-sm rounded" style={{ backgroundColor: '#f8f9fa' }}>
          <h5>Edit Purchase</h5>
          <form onSubmit={handleUpdate}>
            <div className="form-group mb-3">
              <label className="fw-semibold">Order Number</label>
              <input
                type="text"
                name="purchaseNo"
                className="form-control"
                value={editPurchase.purchaseNo}
                onChange={handleInputChange}
                placeholder="Enter order number"
              />
            </div>

            <div className="form-group mb-3">
              <label className="fw-semibold">Vendor Name</label>
              <input
                type="text"
                name="vendorName"
                className="form-control"
                value={editPurchase.vendorName}
                onChange={handleInputChange}
                placeholder="Enter vendor name"
              />
            </div>

            <div className="form-group mb-3">
              <label className="fw-semibold">Total Amount</label>
              <input
                type="text"
                name="totalAmount"
                className="form-control"
                value={editPurchase.totalAmount}
                onChange={handleInputChange}
                placeholder="Enter total amount"
              />
            </div>

            <div className="form-group mb-3">
              <label className="fw-semibold">Items</label>
              <textarea
                name="items"
                className="form-control"
                value={Array.isArray(editPurchase.items) ? editPurchase.items.join(', ') : editPurchase.items}
                onChange={handleInputChange}
                placeholder="Enter items"
              ></textarea>
            </div>

            <div className="d-flex justify-content-center">
              <button type="submit" className="btn btn-success w-50">
                Update
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PurchaseList;
